import type { AppRole } from '../../stores/auth'

export interface INavigationRoute {
  name: string
  displayName: string
  meta: { icon: string; roles?: AppRole[]; permission?: string }
  children?: INavigationRoute[]
}

const FIELD: AppRole[] = ['Admin', 'AreaCoordinator', 'BarangayCoordinator']
const ADMIN: AppRole[] = ['Admin']
const ENCODER: AppRole[] = ['QuickCountEncoder']
const ADMIN_ENCODER: AppRole[] = ['Admin', 'QuickCountEncoder']
const REQUEST_ROLES: AppRole[] = ['Admin', 'RequestEncoder', 'RequestApprover']
// The Reports group spans modules, so its header shows for anyone who can see any report in it.
const REPORT_ROLES: AppRole[] = ['Admin', 'AreaCoordinator', 'BarangayCoordinator', 'RequestEncoder', 'RequestApprover']

export default {
  root: {
    name: '/',
    displayName: 'Home',
  },
  routes: [
    { name: 'dashboard', displayName: 'Dashboard', meta: { icon: 'dashboard', roles: FIELD } },

    // Core voter data (COMELEC)
    { name: 'citizens', displayName: 'Citizens', meta: { icon: 'groups', roles: FIELD } },
    { name: 'scan-card', displayName: 'Scan Citizen Card', meta: { icon: 'qr_code_scanner', roles: FIELD } },
    {
      name: 'projects-group',
      displayName: 'Projects',
      meta: { icon: 'volunteer_activism', roles: FIELD },
      children: [
        { name: 'project-events', displayName: 'Project Events', meta: { icon: 'event_available', roles: FIELD } },
        { name: 'projects', displayName: 'Project Catalogue', meta: { icon: 'category', roles: ADMIN } },
      ],
    },

    // Tagging module (grouped)
    {
      name: 'tagging',
      displayName: 'Tagging',
      meta: { icon: 'how_to_reg', roles: FIELD },
      children: [
        { name: 'tagging-dashboard', displayName: 'Tagging Dashboard', meta: { icon: 'donut_large', roles: FIELD } },
        { name: 'encoding', displayName: 'Encoding', meta: { icon: 'how_to_reg', roles: FIELD } },
        { name: 'citizen-tags', displayName: 'Citizen Tags', meta: { icon: 'sell', roles: FIELD } },
        { name: 'recruiters', displayName: 'Recruiters', meta: { icon: 'person_add', roles: ADMIN } },
        { name: 'tags', displayName: 'Tags', meta: { icon: 'label', roles: ADMIN } },
        { name: 'barangay-tag-targets', displayName: 'Barangay Tag Targets', meta: { icon: 'flag', roles: ADMIN } },
      ],
    },

    // Quick Count module (grouped)
    {
      name: 'quick-count',
      displayName: 'Quick Count',
      meta: { icon: 'poll', roles: ADMIN_ENCODER },
      children: [
        { name: 'quick-count-dashboard', displayName: 'Live Dashboard', meta: { icon: 'leaderboard', roles: ADMIN } },
        { name: 'quick-count-encoder', displayName: 'Encoding', meta: { icon: 'how_to_vote', roles: ENCODER } },
        { name: 'quick-count-entries', displayName: 'Vote Entry', meta: { icon: 'edit_note', roles: ADMIN } },
        { name: 'positions', displayName: 'Positions', meta: { icon: 'list', roles: ADMIN } },
        { name: 'parties', displayName: 'Parties', meta: { icon: 'groups_2', roles: ADMIN } },
        { name: 'candidates', displayName: 'Candidates', meta: { icon: 'badge', roles: ADMIN } },
        { name: 'polling-places', displayName: 'Polling Places', meta: { icon: 'where_to_vote', roles: ADMIN } },
      ],
    },

    {
      name: 'requests-group',
      displayName: 'Requests',
      meta: { icon: 'volunteer_activism', roles: REQUEST_ROLES },
      children: [
        { name: 'requests-dashboard', displayName: 'Requests Dashboard', meta: { icon: 'insights', roles: ADMIN } },
        {
          name: 'requests',
          displayName: 'Requests',
          meta: { icon: 'assignment', roles: REQUEST_ROLES, permission: 'Requests.View' },
        },
        {
          name: 'approval-sheets',
          displayName: 'Approval Sheets',
          meta: { icon: 'fact_check', roles: ADMIN, permission: 'Requests.Batch' },
        },
        { name: 'request-types', displayName: 'Request Types', meta: { icon: 'category', roles: ADMIN } },
        { name: 'budgets', displayName: 'Budgets', meta: { icon: 'account_balance_wallet', roles: ADMIN } },
        {
          name: 'executive-analytics',
          displayName: 'Executive Analytics',
          meta: { icon: 'insights', roles: ADMIN, permission: 'Analytics.View' },
        },
        {
          name: 'coverage-dashboard',
          displayName: 'Coverage Dashboard',
          meta: { icon: 'map', roles: ADMIN, permission: 'Coverage.View' },
        },
      ],
    },

    // §17 Legislative — permission-gated so the custom "Legislative Staff" role sees it.
    {
      name: 'legislative-group',
      displayName: 'Legislative',
      meta: { icon: 'gavel', roles: ADMIN, permission: 'Measures.View' },
      children: [
        {
          name: 'measures',
          displayName: 'Resolutions & Ordinances',
          meta: { icon: 'gavel', roles: ADMIN, permission: 'Measures.View' },
        },
        { name: 'committees', displayName: 'Committees', meta: { icon: 'groups', roles: ADMIN } },
        { name: 'measure-authors', displayName: 'Authors', meta: { icon: 'person_pin', roles: ADMIN } },
      ],
    },

    // Document management — views over attachments, which live on the records themselves.
    {
      name: 'documents-group',
      displayName: 'Documents',
      meta: { icon: 'folder_open', roles: REQUEST_ROLES, permission: 'Requests.View' },
      children: [
        {
          name: 'all-documents',
          displayName: 'All Documents',
          meta: { icon: 'folder_open', roles: REQUEST_ROLES, permission: 'Requests.View' },
        },
        {
          name: 'missing-documents',
          displayName: 'Missing Documents',
          meta: { icon: 'rule_folder', roles: REQUEST_ROLES, permission: 'Requests.View' },
        },
        { name: 'document-types', displayName: 'Document Types', meta: { icon: 'category', roles: ADMIN } },
      ],
    },

    // Reporting — every report in the system lives here, whichever module produces it.
    {
      name: 'reports-group',
      displayName: 'Reports',
      meta: { icon: 'assessment', roles: REPORT_ROLES },
      children: [
        { name: 'citizen-report', displayName: 'Citizen Report', meta: { icon: 'groups', roles: FIELD } },
        { name: 'reports', displayName: 'Tagged Reports', meta: { icon: 'description', roles: FIELD } },
        { name: 'cashflow-report', displayName: 'Cashflow Report', meta: { icon: 'summarize', roles: ADMIN } },
        {
          name: 'measure-reports',
          displayName: 'Legislative Reports',
          meta: { icon: 'assessment', roles: ADMIN, permission: 'Measures.Report' },
        },
      ],
    },

    // Geography / reference data
    { name: 'areas', displayName: 'Areas', meta: { icon: 'public', roles: ADMIN } },
    { name: 'barangays', displayName: 'Barangays', meta: { icon: 'location_city', roles: ADMIN } },
    { name: 'clusters', displayName: 'Clusters', meta: { icon: 'hub', roles: ADMIN } },
    { name: 'precincts', displayName: 'Precincts', meta: { icon: 'pin_drop', roles: ADMIN } },
    { name: 'elections', displayName: 'Elections', meta: { icon: 'ballot', roles: ADMIN } },

    // Office operations (Phase 2)
    {
      name: 'office',
      displayName: 'Office',
      meta: { icon: 'business_center', roles: ADMIN },
      children: [
        { name: 'calendar', displayName: 'Calendar', meta: { icon: 'event', roles: ADMIN } },
        { name: 'assets', displayName: 'Assets', meta: { icon: 'inventory_2', roles: ADMIN } },
        { name: 'inventory', displayName: 'Supplies Inventory', meta: { icon: 'inventory', roles: ADMIN } },
        { name: 'employees', displayName: 'Employees / PDS', meta: { icon: 'badge', roles: ADMIN } },
        { name: 'settings', displayName: 'Settings', meta: { icon: 'settings', roles: ADMIN } },
      ],
    },

    { name: 'users', displayName: 'Users Management', meta: { icon: 'manage_accounts', roles: ADMIN } },
    { name: 'roles', displayName: 'Roles', meta: { icon: 'badge', roles: ADMIN } },
    { name: 'role-permissions', displayName: 'Roles & Permissions', meta: { icon: 'shield_person', roles: ADMIN } },
  ] as INavigationRoute[],
}
